/**
 * useBuyerRegion — client-only buyer "near me" region (web).
 *
 * A buyer can pick their own SA province so the marketplace browse can sort
 * (and optionally filter) listings whose seller is in the same region first.
 * This is a DEGRADED, client-side preference: there is NO backend buyer-region
 * endpoint — the chosen province lives in `localStorage` under {@link KEY} and
 * is reactive across every screen via `useSyncExternalStore` + a tiny pub-sub
 * (mirrors the watchlist store in `useWatchlist.ts` and the per-device
 * read-state model in `notifications/readState.web.ts`).
 *
 * The stored value is one of the 9 SA provinces (see `lib/regions.ts`) or null
 * for "All regions / no preference". A stale/unknown stored string is coerced
 * to null on read via `normalizeRegion`, so callers never see an off-list
 * value. When a buyer geolocation or account-backed region lands, this hook can
 * be repointed and the localStorage store dropped.
 */
import { useSyncExternalStore } from 'react';

import { normalizeRegion, type SaProvince } from '../lib/regions';

/** localStorage key for the buyer's chosen region (a province string, or absent). */
const KEY = 'fse.buyerRegion.v1';

/** Subscribers notified on every mutation so all live screens re-render. */
const subscribers = new Set<() => void>();

/**
 * Cached snapshot. `useSyncExternalStore` requires `getSnapshot` to return a
 * STABLE reference between renders when nothing changed; a `SaProvince | null`
 * is a primitive, so identity is automatic — we just keep the parsed value.
 */
let snapshot: SaProvince | null = readFromStorage();

function readFromStorage(): SaProvince | null {
  if (typeof window === 'undefined') return null;
  try {
    return normalizeRegion(window.localStorage.getItem(KEY));
  } catch {
    return null;
  }
}

function persist(next: SaProvince | null): void {
  snapshot = next;
  if (typeof window !== 'undefined') {
    try {
      if (next == null) window.localStorage.removeItem(KEY);
      else window.localStorage.setItem(KEY, next);
    } catch {
      /* private-mode / quota — the in-memory snapshot still drives this tab */
    }
  }
  for (const cb of subscribers) cb();
}

function subscribe(cb: () => void): () => void {
  subscribers.add(cb);
  // Keep tabs in sync: another tab's write fires a `storage` event here.
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) {
      snapshot = readFromStorage();
      cb();
    }
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    subscribers.delete(cb);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

function getSnapshot(): SaProvince | null {
  return snapshot;
}

/** Server snapshot (SSR / first paint) — always null, this is client state. */
function getServerSnapshot(): SaProvince | null {
  return null;
}

/**
 * Set (or clear) the buyer's region. Pass a province to set it, or null/an
 * off-list value to clear back to "All regions". Off-list inputs are coerced to
 * null so the store never holds an invalid value.
 */
export function setBuyerRegion(next: SaProvince | null | string): void {
  persist(normalizeRegion(next));
}

/** Imperative read of the current buyer region (outside React). */
export function getBuyerRegion(): SaProvince | null {
  return snapshot;
}

export interface UseBuyerRegion {
  /** The chosen province, or null for "All regions" (reactive). */
  region: SaProvince | null;
  /** Set the region (province) or clear it (null). */
  setRegion: (next: SaProvince | null | string) => void;
}

/**
 * Reactive buyer-region hook. Re-renders the consumer whenever the chosen
 * region changes anywhere (this tab or another). `setRegion` is stable.
 */
export function useBuyerRegion(): UseBuyerRegion {
  const region = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return { region, setRegion: setBuyerRegion };
}
